import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useLang } from "../LanguageContext";
import Seo from "../components/Seo";
import { SEGMENT_SEO, SITE_URL } from "../seoConfig";
import { Reveal, Overline } from "../components/Reveal";
import { optImg } from "../lib/img";
import Footer from "../components/Footer";
import Loader from "../components/Loader";

const SegmentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang, setLang, collections, loading } = useLang();
  const item = collections.find((c) => c.id === id);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (loading) return <Loader />;

  if (!item) {
    return (
      <div className="min-h-screen bg-paper flex flex-col items-center justify-center gap-4">
        <p className="font-serif text-2xl text-ink">404</p>
        <Link to="/" className="text-gold hover:underline">← {lang === "sv" ? "Till startsidan" : "Back home"}</Link>
      </div>
    );
  }

  const c = item[lang];
  const gallery = item.gallery || [];

  const seo = (SEGMENT_SEO[id] && SEGMENT_SEO[id][lang]) || {
    title: `${c.name} | Horns Industriservice`,
    description: c.desc,
    keywords: "",
  };
  const imgAbs = item.image ? (item.image.startsWith("http") ? item.image : `${SITE_URL}${item.image}`) : undefined;
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: c.name,
    description: c.desc,
    category: c.short,
    brand: { "@type": "Brand", name: "Horns Industriservice AB" },
    manufacturer: { "@type": "Organization", name: "Horns Industriservice AB" },
    ...(imgAbs ? { image: imgAbs } : {}),
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "sv" ? "Hem" : "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: c.name, item: `${SITE_URL}/produkt/${id}` },
    ],
  };

  return (
    <div className="bg-paper min-h-screen">
      <Seo title={seo.title} description={seo.description} keywords={seo.keywords}
        path={`/produkt/${id}`} image={imgAbs} lang={lang} jsonLd={[productJsonLd, breadcrumbJsonLd]} />
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-paper/90 backdrop-blur-md border-b border-border">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center">
            <img src="/horns-logo.webp" alt="Horns Industriservice" className="h-7 md:h-8 w-auto" />
          </button>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/#products")}
              className="hidden sm:flex items-center gap-2 text-ink/70 hover:text-ink text-sm tracking-[0.12em] uppercase transition-colors">
              <ArrowLeft size={16} /> {t.cta.all}
            </button>
            <div className="flex items-center text-[13px] tracking-[0.15em] font-light text-ink/60">
              <button onClick={() => setLang("sv")} className={"px-1 " + (lang === "sv" ? "text-ink font-medium" : "hover:text-gold")}>SV</button>
              <span className="opacity-40">/</span>
              <button onClick={() => setLang("en")} className={"px-1 " + (lang === "en" ? "text-ink font-medium" : "hover:text-gold")}>EN</button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative h-[52vh] min-h-[380px] overflow-hidden bg-ink">
        <motion.img initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: [0.19, 1, 0.22, 1] }}
          src={optImg(item.image, 1600, 74)} alt={c.name} fetchpriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-12">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="font-serif text-white/40 text-lg mb-3">{item.number}</motion.span>
          <Overline dark>{c.short}</Overline>
          <h1 className="font-serif text-white font-light leading-[1] text-5xl md:text-7xl mt-4">{c.name}</h1>
        </div>
      </section>

      {/* Intro description + points */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-[1.2fr_1fr] gap-12 md:gap-20">
        <Reveal>
          <p className="font-serif text-ink font-light text-2xl md:text-3xl leading-[1.4]">{c.desc}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <ul className="space-y-4 md:pt-2">
            {c.points.map((p, i) => (
              <li key={i} className="flex items-center gap-4 text-ink border-b border-border pb-4">
                <span className="h-8 w-8 flex items-center justify-center border border-gold text-gold rounded-full shrink-0">
                  <Check size={15} strokeWidth={1.5} />
                </span>
                <span className="font-light text-lg">{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* Gallery with technical descriptions */}
      {gallery.length > 0 && (
        <section className="bg-paper-warm py-16 md:py-24">
          <div className="max-w-[1500px] mx-auto px-6 md:px-10">
            <Reveal>
              <Overline>{lang === "sv" ? "Bildgalleri & teknik" : "Gallery & technical detail"}</Overline>
            </Reveal>
            <div className="mt-12 space-y-16 md:space-y-28">
              {gallery.map((g, i) => {
                const gc = g[lang] || {};
                const flip = i % 2 === 1;
                return (
                  <Reveal key={i} y={60}>
                    <div className={"grid md:grid-cols-2 gap-8 md:gap-16 items-center " + (flip ? "md:[direction:rtl]" : "")}>
                      <div className="relative aspect-[4/3] overflow-hidden group [direction:ltr]">
                        <img src={optImg(g.image, 1000)} alt={gc.title ? `${gc.title} — ${c.name}` : c.name} loading="lazy" decoding="async"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <div className="absolute inset-0 ring-1 ring-inset ring-black/10" />
                      </div>
                      <div className="[direction:ltr]">
                        <span className="font-serif text-gold text-xl">{String(i + 1).padStart(2, "0")}</span>
                        <h3 className="font-serif text-ink font-light text-3xl md:text-4xl mt-2 leading-tight">{gc.title}</h3>
                        <p className="mt-5 text-ink/65 text-lg font-light leading-relaxed">{gc.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-ink text-white py-20 md:py-28">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-serif font-light text-3xl md:text-5xl max-w-2xl leading-tight">{t.contact.title}</h2>
          <button onClick={() => navigate("/#contact")}
            className="group flex items-center gap-3 bg-gold hover:bg-gold-light text-white px-9 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors w-fit">
            {t.cta.contactUs}
            <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SegmentPage;
